import React from 'react';
import Table, { CellProps } from 'rsuite/Table';
import { usePaginationQs } from '../../../../utils/usePagination';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { trpc, RouterInput } from '../../../../api/trpc';
import {
  CleanUser,
  RbacObject,
  TempAccessSession,
} from '../../../../export-types/cleaned-types';
import { WhisperSpeaker } from '../../../../utils/whisper-speaker';
import { Rbac } from '../../../common/Rbac';
import Whisper from 'rsuite/Whisper';
import IconButton from 'rsuite/IconButton';
import { ACCESSES } from '../../../../consts/db-values';
import Pagination from 'rsuite/Pagination';
import { Dropdown, Popover } from 'rsuite';
import moment from 'moment';
import { NavLink } from 'react-router-dom';
import { TempAccessSessionModal } from './temp-access-session-modal';
import { ModalsService } from '../../../../services/modals.service';
import { ConfirmModal } from '../../../common/confirm-modal';
import { AlertService } from '../../../../services/alert.service';

type Props = {
  status: 'archived' | 'all' | 'plan' | 'active';
};

type SessionProps = TempAccessSession & {
  user: CleanUser;
};

export const TempAccessSessionTab = (props: Props) => {
  const defaultTake = 20;
  const { take, skip, handleSetPage, handleChangeLimit, page } =
    usePaginationQs(defaultTake);

  const input = {
    take,
    skip,
    status: props.status !== 'all' ? props.status : undefined,
  };
  const { data, isLoading, mutate } = useSWR(
    ['tempAccessSession.list', input],
    () => trpc.tempAccessSession.list.query(input),
  );

  const { trigger: deleteTrigger } = useSWRMutation(
    ['tempAccessSession.delete'],
    (_k, { arg }: { arg: RouterInput['tempAccessSession']['delete'] }) =>
      trpc.tempAccessSession.delete.mutate(arg),
  );
  const { trigger: denyTrigger } = useSWRMutation(
    ['tempAccessSession.deny'],
    (_k, { arg }: { arg: RouterInput['tempAccessSession']['deny'] }) =>
      trpc.tempAccessSession.deny.mutate(arg),
  );

  const AccessesCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<SessionProps>) => {
    if (!rowData) return null;
    const str = rowData.accesses
      ? rowData.accesses
          .map((a) => ACCESSES[a as keyof typeof ACCESSES] ?? a)
          .join(' / ')
      : '';
    return <Table.Cell {...props}>{str}</Table.Cell>;
  };

  const StatusCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<SessionProps>) => {
    if (!rowData) return null;
    if (rowData.isDenied) return <Table.Cell {...props}>Отозвано</Table.Cell>;
    if (
      rowData.schedule.some((date) =>
        moment(date.startedAt).isAfter(new Date()),
      )
    )
      return <Table.Cell {...props}>Запланирован</Table.Cell>;

    if (
      rowData.schedule.every((date) =>
        moment(date.finishedAt).isBefore(new Date()),
      )
    )
      return <Table.Cell {...props}>Истёк</Table.Cell>;

    return <Table.Cell {...props}>Активен</Table.Cell>;
  };

  const DateCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<SessionProps>) => {
    if (!rowData) return null;

    let startedAt;
    let finishedAt;
    if (rowData.schedule.length === 1) {
      startedAt = rowData.schedule[0].startedAt;
      finishedAt = rowData.schedule[0].finishedAt;
    } else {
      if (
        rowData.schedule.every((date) =>
          moment(date.finishedAt).isBefore(new Date()),
        )
      ) {
        startedAt = rowData.schedule[rowData.schedule.length - 1].startedAt;
        finishedAt = rowData.schedule[rowData.schedule.length - 1].finishedAt;
      } else {
        if (
          rowData.schedule.every((date) =>
            moment(date.startedAt).isAfter(new Date()),
          )
        ) {
          startedAt = rowData.schedule[0].startedAt;
          finishedAt = rowData.schedule[0].finishedAt;
        } else {
          const data = rowData.schedule.find((date) =>
            moment(date.startedAt).isAfter(new Date()),
          );
          startedAt = data?.startedAt;
          finishedAt = data?.finishedAt;
        }
      }
    }

    return (
      <Table.Cell {...props}>
        {moment(startedAt).format('DD.MM.YYYY HH:mm')} -{' '}
        {moment(finishedAt).format('DD.MM.YYYY HH:mm')}
      </Table.Cell>
    );
  };

  const renderMenu =
    (session: SessionProps): WhisperSpeaker =>
    ({ onClose, left, top, className }, ref) => {
      const handleSelect = async (eventKey: string | undefined) => {
        onClose();
        if (eventKey === '1') {
          await ModalsService.createModal(TempAccessSessionModal, {
            session,
            user: { ...session.user, id: session.user.id },
            mutate,
          });
        }

        if (eventKey === '2') {
          await ModalsService.createModal(ConfirmModal, {
            title: 'Вы уверены, что хотите отозвать?',
            buttonText: 'Отозвать',
          }).then(async () => {
            await denyTrigger({ id: session.id });
            mutate();
            AlertService.success('Отозвано');
          });
        }

        if (eventKey === '3') {
          await ModalsService.createModal(ConfirmModal, {
            title: 'Вы уверены, что хотите удалить?',
            buttonText: 'Удалить',
          }).then(async () => {
            await deleteTrigger({ id: session.id });
            mutate();
            AlertService.success('Удалено');
          });
        }
      };

      return (
        <Popover ref={ref} className={className} style={{ left, top }} full>
          <Dropdown.Menu onSelect={handleSelect}>
            <Dropdown.Item eventKey={'1'}>
              <i className="bi bi-pencil" />
              <span className="ps-2">Изменить</span>
            </Dropdown.Item>
            <Dropdown.Item eventKey={'2'}>
              <i className="bi bi-cross" />
              <span className="ps-2">Отозвать</span>
            </Dropdown.Item>
            <Dropdown.Item eventKey={'3'}>
              <i className="bi bi-trash" />
              <span className="ps-2">Удалить</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Popover>
      );
    };

  const ActionCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<SessionProps>) => {
    return (
      rowData && (
        <Table.Cell
          {...props}
          className="link-group no-padding-cell pt-2"
          align="right"
        >
          <Rbac object={RbacObject.TempAccessSession} action={'write'}>
            <Whisper
              placement="leftStart"
              trigger="click"
              speaker={renderMenu(rowData)}
            >
              <IconButton
                appearance="subtle"
                icon={<i className="bi bi-three-dots" />}
              />
            </Whisper>
          </Rbac>
        </Table.Cell>
      )
    );
  };

  const total = data?.total;
  const items = data?.items;

  return (
    <>
      <div className="d-flex flex-row">
        <ul className="nav nav-pills mb-4">
          <li className="nav-item">
            <NavLink
              to="/users/temp-access-sessions/all"
              className="nav-link text-decoration-none"
            >
              Все
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink
              to="/users/temp-access-sessions/active"
              className="nav-link text-decoration-none"
            >
              Активные
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink
              to="/users/temp-access-sessions/plan"
              className="nav-link text-decoration-none"
            >
              Запланированные
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink
              to="/users/temp-access-sessions/archived"
              className="nav-link text-decoration-none"
            >
              Архивные
            </NavLink>
          </li>
        </ul>
      </div>
      <Table data={items} headerHeight={57} autoHeight loading={isLoading}>
        <Table.Column fullText width={150}>
          <Table.HeaderCell>Имя</Table.HeaderCell>
          <Table.Cell dataKey={'user.name'} />
        </Table.Column>
        <Table.Column fullText flexGrow={1}>
          <Table.HeaderCell>Номер телефона</Table.HeaderCell>
          <Table.Cell dataKey="user.phone" />
        </Table.Column>
        <Table.Column fullText flexGrow={1}>
          <Table.HeaderCell>Причина</Table.HeaderCell>
          <Table.Cell dataKey="reason" />
        </Table.Column>
        <Table.Column fullText flexGrow={1}>
          <Table.HeaderCell>Статус</Table.HeaderCell>
          <StatusCell />
        </Table.Column>
        <Table.Column fullText flexGrow={1}>
          <Table.HeaderCell>Период действия</Table.HeaderCell>
          <DateCell></DateCell>
        </Table.Column>
        <Table.Column fullText flexGrow={1}>
          <Table.HeaderCell>Права</Table.HeaderCell>
          <AccessesCell />
        </Table.Column>
        <Table.Column>
          <Table.HeaderCell> </Table.HeaderCell>
          <ActionCell />
        </Table.Column>
      </Table>

      {total && total > take ? (
        <div className="mt-4 pb-4">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="md"
            layout={['pager']}
            total={total}
            limitOptions={[15, 20]}
            limit={take}
            activePage={page}
            onChangePage={handleSetPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      ) : null}
    </>
  );
};
